/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { css, jsx } from '@compiled/react';
import { useState } from 'react';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { UI_EVENT_TYPE } from '@atlaskit/analytics-gas-types';
import Button from '@atlaskit/button';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Text } from '@atlaskit/primitives/compiled';
import { useIntl, defineMessages } from 'react-intl-next';
import type { EgressFilteringService } from '@forge/egress';

import ModalDialog, {
	ModalTransition,
	ModalFooter as AKModalFooter,
	ModalBody,
	ModalTitle,
	ModalHeader,
} from '@atlaskit/modal-dialog';

import { token } from '@atlaskit/tokens';
import type { Extension } from '../../../web-client';
import { FORGE_UI_ANALYTICS_CHANNEL } from '../../../analytics';

import { type NavigatePayload, BridgeClientError } from '..';

import { navigate } from './navigate';
import { getOrigin } from './getOrigin';
import { useEgressPermissionService } from '../../../utils/useEgressPermissionService';
import { makeUrl } from './makeUrl';
import { TRUSTED_ROOT_DOMAINS } from '../constants/trustedDomains';

type ModalState =
	| {
			onCancel: () => void;
			onConfirm: () => void;
			state: 'open';
			url: URL;
	  }
	| {
			state: 'closed';
	  };

const cssActionItem = css({
	flex: '1 0 auto',
	marginTop: 0,
	marginRight: token('space.050'),
	marginBottom: 0,
	marginLeft: token('space.050'),
});

const messages = defineMessages({
	navigationWarningTitle: {
		id: 'forge-ui.custom-ui.bridge.messages.navigation-warning-title',
		defaultMessage: 'Opening external page on {hostname}',
		description:
			'Title for the modal that warns the user that they are about to open an external page',
	},
	navigationWarningBody: {
		id: 'forge-ui.custom-ui.bridge.messages.navigation-warning-body',
		defaultMessage:
			'is sending you to an external page. Ensure you trust that page before you continue.',
		description:
			'Body for the modal that warns the user that they are about to open an external page',
	},
	navigationWarningBodyNoAppName: {
		id: 'forge-ui.custom-ui.bridge.messages.navigation-warning-body-no-app-name',
		defaultMessage:
			'This app is sending you to an external page. Ensure you trust that page before you continue.',
		description:
			'Body for the modal that warns the user that they are about to open an external page without an extension name',
	},
	navigationWarningAppSecurityLink: {
		id: 'forge-ui.custom-ui.bridge.messages.navigation-warning-app-security-link',
		defaultMessage: 'Learn more about app security',
		description: 'Text for the link that takes the user to the app security page',
	},
	navigationWarningCancelButton: {
		id: 'forge-ui.custom-ui.bridge.messages.navigation-warning-cancel-button',
		defaultMessage: 'Cancel',
		description: 'Text for the button that cancels the modal',
	},
	navigationWarningContinueButton: {
		id: 'forge-ui.custom-ui.bridge.messages.navigation-warning-continue-button',
		defaultMessage: 'Continue',
		description: 'Text for the button that continues the modal',
	},
});

export function useNavigation({
	extension,
	push,
}: {
	extension: Pick<Extension, 'properties' | 'egress'>;
	push?: (path: string, state?: any) => void;
}) {
	const [modalState, setModalState] = useState<ModalState>({ state: 'closed' });
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const egressService = useEgressPermissionService('FETCH_CLIENT_SIDE', extension.egress);
	const intl = useIntl();

	return {
		onNavigate: async ({ type, url: urlString }: NavigatePayload): Promise<void> => {
			const url = makeUrl(urlString);
			const linkType = getLinkType(url, egressService);
			if (linkType === 'invalid' || !url) {
				throw new BridgeClientError(`link is invalid: ${urlString}`);
			}
			if (linkType === 'trusted') {
				navigate(url.href, type, push);
				return;
			}
			const analyticsAttributes = getAnalyticsAttributes(url);
			return new Promise((resolve, reject) => {
				// TODO: remove this event once we have switched over to the new event schema
				// https://ecosystem-platform.atlassian.net/browse/EXT-2196
				createAnalyticsEvent({
					eventType: UI_EVENT_TYPE,
					data: {
						action: 'viewed',
						actionSubject: 'externalLinkModal',
						attributes: analyticsAttributes,
					},
				}).fire(FORGE_UI_ANALYTICS_CHANNEL);
				setModalState({
					state: 'open',
					url,
					onConfirm: () => {
						navigate(url.href, type, push);
						// TODO: remove this event once we have switched over to the new event schema
						// https://ecosystem-platform.atlassian.net/browse/EXT-2196
						createAnalyticsEvent({
							eventType: UI_EVENT_TYPE,
							data: {
								action: 'approved',
								actionSubject: 'externalLinkModal',
								attributes: analyticsAttributes,
							},
						}).fire(FORGE_UI_ANALYTICS_CHANNEL);
						resolve();
					},
					onCancel: () => {
						// TODO: remove this event once we have switched over to the new event schema
						// https://ecosystem-platform.atlassian.net/browse/EXT-2196
						createAnalyticsEvent({
							eventType: UI_EVENT_TYPE,
							data: {
								action: 'cancelled',
								actionSubject: 'externalLinkModal',
								attributes: analyticsAttributes,
							},
						}).fire(FORGE_UI_ANALYTICS_CHANNEL);
						reject(new BridgeClientError('cancelled'));
					},
				});
			});
		},

		getModalJsx: (): JSX.Element => {
			if (modalState.state === 'closed') {
				return <ModalTransition />;
			}
			const ModalFooter = () => {
				return (
					<AKModalFooter>
						<div
							// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage -- to avoid import of css module from @compiled/react
							css={{
								display: 'flex',
								justifyContent: 'space-between',
								width: '100%',
							}}
						>
							<Button
								css={{ padding: 0, '> span': { margin: 0 } }}
								href="https://go.atlassian.com/forge-help-app-security"
								appearance="subtle-link"
								target="_blank"
								rel="noopener noreferrer"
								// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx
							>
								{intl.formatMessage(messages.navigationWarningAppSecurityLink)}
							</Button>
							<div
								// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
								css={css({
									display: 'inline-flex',
									marginTop: 0,
									marginRight: token('space.negative.050'),
									marginBottom: 0,
									marginLeft: token('space.negative.050'),
								})}
							>
								<div css={cssActionItem}>
									<Button
										appearance="subtle"
										onClick={() => {
											setModalState({ state: 'closed' });
											modalState.onCancel();
										}}
									>
										{intl.formatMessage(messages.navigationWarningCancelButton)}
									</Button>
								</div>
								<div css={cssActionItem}>
									<Button
										appearance="warning"
										onClick={() => {
											modalState.onConfirm();
											setModalState({ state: 'closed' });
										}}
									>
										{intl.formatMessage(messages.navigationWarningContinueButton)}
									</Button>
								</div>
							</div>
						</div>
					</AKModalFooter>
				);
			};
			return (
				<ModalTransition>
					<ModalDialog
						onClose={() => {
							setModalState({ state: 'closed' });
							modalState.onCancel();
						}}
					>
						<ModalHeader>
							<ModalTitle appearance="warning">
								{intl.formatMessage(messages.navigationWarningTitle, {
									hostname: modalState.url.hostname,
								})}
							</ModalTitle>
						</ModalHeader>

						<ModalBody>
							{extension.properties?.title ? (
								<Text as="p">
									<Text as="strong">{extension.properties.title}</Text>{' '}
									{intl.formatMessage(messages.navigationWarningBody)}
								</Text>
							) : (
								<Text as="p">{intl.formatMessage(messages.navigationWarningBodyNoAppName)}</Text>
							)}
							{/* eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage -- to avoid import of css module from @compiled/react */}
							<Text as="p" maxLines={1}>
								{modalState.url.href}
							</Text>
						</ModalBody>
						<ModalFooter />
					</ModalDialog>
				</ModalTransition>
			);
		},
	};
}

const getRootDomain = (url: URL) => {
	const hostnameParts = url.hostname.split('.');
	// Root domain consists of the last two parts of the hostname
	if (hostnameParts.length > 1) {
		return hostnameParts.slice(-2).join('.');
	} else {
		// If there's only one part, return it (e.g., 'localhost')
		return url.hostname;
	}
};

// Exported for testing only
export function getLinkType(
	url: URL | undefined,
	egressService: EgressFilteringService,
): 'external' | 'trusted' | 'invalid' {
	if (!url) {
		return 'invalid';
	}

	if (url.protocol === 'mailto:') {
		return 'trusted';
	}
	if (url.protocol === 'tel:') {
		return 'trusted';
	}

	try {
		// URL is listed under permissions in manifest
		if (egressService.isValidUrl(url.href)) {
			return 'trusted';
		}

		// URL is from same origin
		if (url.origin === getOrigin()) {
			return 'trusted';
		}

		// URL is under a trusted atlassian domain
		const rootDomain = getRootDomain(url);
		if (TRUSTED_ROOT_DOMAINS.includes(rootDomain)) {
			return 'trusted';
		}

		if (['https:', 'http:'].includes(url.protocol)) {
			return 'external';
		}
	} catch {}
	return 'invalid';
}

// Exported for testing only
export function getAnalyticsAttributes(url: URL): Record<string, any> {
	// Don't count the initial `?` in the query string length.
	const queryStringSize = url.search.length > 1 ? url.search.length - 1 : 0;
	return {
		url: url.href,
		queryStringSize,
	};
}
